import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { Plus, Trash2, Eye, Edit2 } from 'lucide-react';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [filters, setFilters] = useState({
    retailer_id: '',
    status: '',
    payment_status: '',
    start_date: '',
    end_date: ''
  });
  const [formData, setFormData] = useState({
    retailer_id: '',
    order_date: '',
    status: 'Placed',
    payment_term_id: '',
    payment_status: 'Pending',
    notes: '',
    items: []
  });

  const orderStatuses = ['Placed', 'Invoiced', 'Processing', 'Packed', 'Shipped', 'Delivered'];
  const paymentStatuses = ['Pending', 'Paid'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (filterParams = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key]) {
          queryParams.append(key, filterParams[key]);
        }
      });
      
      const [ordersRes, retailersRes, productsRes, termsRes] = await Promise.all([
        api.get(`/orders${queryParams.toString() ? '?' + queryParams.toString() : ''}`),
        api.get('/retailers'),
        api.get('/products'),
        api.get('/payments/terms')
      ]);
      setOrders(ordersRes.data);
      setRetailers(retailersRes.data);
      setProducts(productsRes.data);
      setPaymentTerms(termsRes.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch data');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOrder) {
        await api.put(`/orders/${editingOrder.id}`, formData);
        toast.success('Order updated successfully');
      } else {
        await api.post('/orders', formData);
        toast.success('Order created successfully');
      }
      fetchData(filters);
      resetForm();
    } catch (error) {
      toast.error(editingOrder ? 'Failed to update order' : 'Failed to create order');
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await api.put(`/orders/${orderId}`, { status: newStatus });
      toast.success('Order status updated');
      fetchData(filters);
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const handlePaymentStatusUpdate = async (orderId, newStatus) => {
    try {
      await api.put(`/orders/${orderId}`, { payment_status: newStatus });
      toast.success('Payment status updated');
      fetchData(filters);
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        await api.delete(`/orders/${id}`);
        toast.success('Order deleted successfully');
        fetchData(filters);
      } catch (error) {
        toast.error('Failed to delete order');
      }
    }
  };

  const viewOrderDetails = async (order) => {
    try {
      const response = await api.get(`/orders/${order.id}`);
      setSelectedOrder(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      toast.error('Failed to fetch order details');
    }
  };

  const handleEdit = async (order) => {
    try {
      const response = await api.get(`/orders/${order.id}`);
      const orderData = response.data;
      
      setEditingOrder(orderData);
      setFormData({
        retailer_id: orderData.retailer?._id || orderData.retailer?.id || '',
        order_date: orderData.order_date ? orderData.order_date.split('T')[0] : '',
        status: orderData.status,
        payment_term_id: orderData.payment_term?._id || orderData.payment_term?.id || '',
        payment_status: orderData.payment_status,
        notes: orderData.notes || '',
        items: orderData.items?.map(item => ({
          product_id: item.product?._id || item.product?.id || item.product,
          quantity: item.quantity,
          unit_price: item.unit_price
        })) || []
      });
      setShowModal(true);
    } catch (error) {
      toast.error('Failed to fetch order details for editing');
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingOrder(null);
    setFormData({
      retailer_id: '',
      order_date: '',
      status: 'Placed',
      payment_term_id: '',
      payment_status: 'Pending',
      notes: '',
      items: []
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: '', quantity: 1, unit_price: 0 }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      if (product) {
        newItems[index].unit_price = product.price;
      }
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Placed': 'badge-info',
      'Invoiced': 'badge-warning',
      'Processing': 'badge-warning',
      'Packed': 'badge-warning',
      'Shipped': 'badge-info',
      'Delivered': 'badge-success'
    };
    return colors[status] || 'badge-secondary';
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const applyFilters = () => {
    setLoading(true);
    fetchData(filters);
  };

  const clearFilters = () => {
    setFilters({
      retailer_id: '',
      status: '',
      payment_status: '',
      start_date: '',
      end_date: ''
    });
    setLoading(true);
    fetchData();
  };

  if (loading) {
    return <div className="container">Loading orders...</div>;
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>Orders</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={16} style={{ marginRight: '5px' }} />
          Create Order
        </button>
      </div>

      {/* Filters Section */}
      <div className="card" style={{ marginBottom: '20px', padding: '15px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: '14px', color: '#666', minWidth: '50px' }}>Filters:</strong>
          
          <select
            name="retailer_id"
            value={filters.retailer_id}
            onChange={handleFilterChange}
            style={{ 
              minWidth: '140px', 
              fontSize: '14px', 
              padding: '6px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white'
            }}
          >
            <option value="">All Retailers</option>
            {retailers.map(retailer => (
              <option key={retailer.id} value={retailer.id}>
                {retailer.name}
              </option>
            ))}
          </select>

          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            style={{ 
              minWidth: '120px', 
              fontSize: '14px', 
              padding: '6px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white'
            }}
          >
            <option value="">All Statuses</option>
            {orderStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>

          <select
            name="payment_status"
            value={filters.payment_status}
            onChange={handleFilterChange}
            style={{ 
              minWidth: '140px', 
              fontSize: '14px', 
              padding: '6px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white'
            }}
          >
            <option value="">All Payment Status</option>
            {paymentStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>

          <input
            type="date"
            name="start_date"
            value={filters.start_date}
            onChange={handleFilterChange}
            placeholder="Start Date"
            style={{ 
              width: '140px', 
              fontSize: '14px', 
              padding: '6px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white'
            }}
          />

          <input
            type="date"
            name="end_date"
            value={filters.end_date}
            onChange={handleFilterChange}
            placeholder="End Date"
            style={{ 
              width: '140px', 
              fontSize: '14px', 
              padding: '6px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white'
            }}
          />

          <button 
            className="btn btn-primary" 
            onClick={applyFilters}
            style={{ padding: '6px 12px', fontSize: '14px' }}
          >
            Apply
          </button>
          
          <button 
            className="btn btn-secondary" 
            onClick={clearFilters}
            style={{ padding: '6px 12px', fontSize: '14px' }}
          >
            Clear
          </button>
        </div>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Order #</th>
              <th>Retailer</th>
              <th>Date</th>
              <th>Status</th>
              <th>Payment Status</th>
              <th>Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(order => (
              <tr key={order.id}>
                <td>{order.order_number}</td>
                <td>{order.retailer?.name || 'N/A'}</td>
                <td>{new Date(order.order_date).toLocaleDateString()}</td>
                <td>
                  <select
                    value={order.status}
                    onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
                    className={`badge ${getStatusColor(order.status)}`}
                    style={{ border: 'none', cursor: 'pointer' }}
                  >
                    {orderStatuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    value={order.payment_status}
                    onChange={(e) => handlePaymentStatusUpdate(order.id, e.target.value)}
                    className={`badge ${order.payment_status === 'Paid' ? 'badge-success' : 'badge-warning'}`}
                    style={{ border: 'none', cursor: 'pointer' }}
                  >
                    {paymentStatuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </td>
                <td>₹{Math.round(order.total_amount) || 0}</td>
                <td>
                  <button
                    onClick={() => viewOrderDetails(order)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: '10px' }}
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    onClick={() => handleEdit(order)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: '10px' }}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(order.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545' }}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 className="modal-title">{editingOrder ? 'Edit Order' : 'Create New Order'}</h2>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Retailer *</label>
                  <select
                    name="retailer_id"
                    className="form-control"
                    value={formData.retailer_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Retailer</option>
                    {retailers.map(retailer => (
                      <option key={retailer.id} value={retailer.id}>
                        {retailer.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Order Date</label>
                  <input
                    type="date"
                    name="order_date"
                    className="form-control"
                    value={formData.order_date}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Payment Terms</label>
                  <select
                    name="payment_term_id"
                    className="form-control"
                    value={formData.payment_term_id}
                    onChange={handleChange}
                  >
                    <option value="">Select Payment Terms</option>
                    {paymentTerms.map(term => (
                      <option key={term.id} value={term.id}>
                        {term.term_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Order Items</label>
                {formData.items.map((item, index) => (
                  <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <select
                      className="form-control"
                      value={item.product_id}
                      onChange={(e) => updateItem(index, 'product_id', e.target.value)}
                      required
                    >
                      <option value="">Select Product</option>
                      {products.map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name} - ₹{product.price}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Quantity"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      min="1"
                      required
                      style={{ width: '100px' }}
                    />
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Price"
                      value={item.unit_price}
                      onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                      step="0.01"
                      required
                      style={{ width: '100px' }}
                    />
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => removeItem(index)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" className="btn btn-secondary" onClick={addItem}>
                  Add Item
                </button>
              </div>

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  name="notes"
                  className="form-control"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingOrder ? 'Update Order' : 'Create Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDetailsModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Order Details - {selectedOrder.order_number}</h2>
            </div>
            
            <div>
              <p><strong>Retailer:</strong> {selectedOrder.retailer?.name || 'N/A'}</p>
              <p><strong>Date:</strong> {new Date(selectedOrder.order_date).toLocaleDateString()}</p>
              <p><strong>Status:</strong> <span className={`badge ${getStatusColor(selectedOrder.status)}`}>{selectedOrder.status}</span></p>
              <p><strong>Payment Status:</strong> <span className={`badge ${selectedOrder.payment_status === 'Paid' ? 'badge-success' : 'badge-warning'}`}>{selectedOrder.payment_status}</span></p>
              <p><strong>Payment Terms:</strong> {selectedOrder.payment_term?.term_name || 'N/A'}</p>
              
              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <>
                  <h4 style={{ marginTop: '20px' }}>Items</h4>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Unit Price</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items.map((item, index) => (
                        <tr key={index}>
                          <td>{item.product?.name || 'N/A'}</td>
                          <td>{item.quantity}</td>
                          <td>₹{item.unit_price}</td>
                          <td>₹{item.total_price}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
              
              <p><strong>Total Amount:</strong> ₹{Math.round(selectedOrder.total_amount) || 0}</p>
              {selectedOrder.notes && <p><strong>Notes:</strong> {selectedOrder.notes}</p>}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;